// Vorbelegungen der internen Schadenmeldung (reine Funktionen):
//  - „Wo“ aus der Anschrift des gewählten Risikoobjekts,
//  - „Schadenmelder“ aus dem Kontakt des Schadens.
// Vorbelegt wird nur in leere Felder — eine Eingabe wird nie überschrieben.
import type { BigAnswers } from "../../schadenmeldung-big/types";

export interface Anschrift {
  strasse: string;
  plz: string;
  ort: string;
}

const text = (v: unknown): string => (typeof v === "string" ? v.trim() : typeof v === "number" ? String(v) : "");

/**
 * Freitext-Standort („Werk Essen, Musterstraße 12, 45127 Essen“) → Anschrift.
 * Maßgeblich ist der Teil „PLZ Ort“; der Teil davor zählt als Straße, wenn er
 * eine Hausnummer trägt (sonst ist es nur ein Standortname).
 */
export function anschriftAusFreitext(freitext: string): Anschrift | null {
  const teile = freitext.split(",").map((t) => t.trim()).filter(Boolean);
  for (let i = teile.length - 1; i >= 0; i--) {
    const m = /^(?:D-)?(\d{5})\s+(.+)$/.exec(teile[i]);
    if (!m) continue;
    const ort = m[2].replace(/\s*\(.*\)\s*$/, "").trim();
    const davor = i > 0 ? teile[i - 1] : "";
    return { strasse: /\d/.test(davor) ? davor : "", plz: m[1], ort };
  }
  return null;
}

/**
 * Anschrift eines Risikoobjekts aus seinen Merkmalen. In den Daten liegt sie
 * auf zwei Arten: als `street`/`postalCode`/`city` (Immobilienmappe) oder als
 * EIN Freitext-Merkmal `Risikoort` (Sachwerte-Erfassung). Sonst `null`.
 */
export function risikoAnschrift(row: any): Anschrift | null {
  const merkmale: any[] = Array.isArray(row?.characteristics) ? row.characteristics : [];
  const wert = (...namen: string[]): string => {
    for (const n of namen) {
      const treffer = merkmale.find((m) => m?.nameBezeichnung === n);
      const v = text(treffer?.riskCharacteristicValue);
      if (v) return v;
    }
    return "";
  };
  const hausnummer = wert("houseNumber", "Hausnummer");
  const strasse = [wert("street", "Straße"), hausnummer].filter(Boolean).join(" ");
  const plz = wert("postalCode", "PLZ");
  const ort = wert("city", "Ort");
  if (strasse || plz || ort) return { strasse, plz, ort };
  const freitext = wert("Risikoort");
  return freitext ? anschriftAusFreitext(freitext) : null;
}

/** Einzeiler einer Anschrift („Straße, PLZ Ort“), leer ohne Anschrift. */
export function anschriftText(a: Anschrift | null): string {
  return a ? [a.strasse, [a.plz, a.ort].filter(Boolean).join(" ")].filter(Boolean).join(", ") : "";
}

/**
 * Passt ein Risiko zur Schadenart? Die Risikoart steht in `RiskObject.riskType`
 * („Immobilie“, „KFZ“, „Betriebsvermögen“, „Maschine“ …): Gebäude → Immobilien,
 * Kfz → Fahrzeuge, Sonstiges (und ohne Schadenart) → alles.
 */
export function risikoPasstZurSchadenart(
  damageType: BigAnswers["damageType"],
  riskType: string | null | undefined,
): boolean {
  const t = (riskType ?? "").toLowerCase();
  if (damageType === "gebaeude") return t.includes("immo") || t.includes("gebäude") || t.includes("gebaeude");
  if (damageType === "kfz") return t.includes("kfz") || t.includes("fahrzeug");
  return true;
}

const WO_FELDER = {
  gebaeude: ["gebStrasse", "gebPlz", "gebOrt"],
  sonstiges: ["sonStrasse", "sonPlz", "sonOrt"],
} as const;

/**
 * Patch für den „Wo“-Schritt aus der Anschrift des Risikos. Gefüllt wird, wenn
 * Straße, PLZ und Ort noch alle leer sind — oder noch genau dem Stand
 * entsprechen, der aus dem zuvor gewählten Risiko (`vorher`) vorbelegt wurde
 * (dann hat niemand etwas geändert). Kfz: kein Vorbelegen (der Unfallort ist
 * nicht der Standort des Fahrzeugs).
 */
export function woVorbelegung(
  a: BigAnswers,
  adr: Anschrift | null,
  vorher: Anschrift | null = null,
): Partial<BigAnswers> | null {
  if (!adr || (a.damageType !== "gebaeude" && a.damageType !== "sonstiges")) return null;
  const [s, p, o] = WO_FELDER[a.damageType];
  const ist = { strasse: a[s].trim(), plz: a[p].trim(), ort: a[o].trim() };
  const leer = !ist.strasse && !ist.plz && !ist.ort;
  const unberuehrt =
    vorher !== null && ist.strasse === vorher.strasse && ist.plz === vorher.plz && ist.ort === vorher.ort;
  if (!leer && !unberuehrt) return null;
  if (ist.strasse === adr.strasse && ist.plz === adr.plz && ist.ort === adr.ort) return null;
  return { [s]: adr.strasse, [p]: adr.plz, [o]: adr.ort } as Partial<BigAnswers>;
}

/** Sind die Angaben zum Schadenmelder noch unberührt? */
export function melderLeer(a: BigAnswers): boolean {
  return ![a.melderVorname, a.melderName, a.melderTel, a.melderHandy, a.melderEmail].some((v) => v.trim());
}

/**
 * Patch für den Schadenmelder aus einer Kontakt-Zeile (`GET contact/:id`),
 * soweit Daten da sind. Bei einer Firma nur Telefon und E-Mail — der Melder
 * ist eine Person, nicht die Firma.
 */
export function melderAusKontakt(row: any): Partial<BigAnswers> | null {
  if (!row) return null;
  const liste: any[] = Array.isArray(row.communications) ? row.communications : [];
  const komm = (typ: string): string => {
    const passend = liste.filter((c) => c?.type === typ && text(c?.value));
    return text((passend.find((c) => c?.isDefault || c?.primary) ?? passend[0])?.value);
  };
  const firma = row.istFirma === true || row?.type?.type === "Firma";
  const patch: Partial<BigAnswers> = {};
  if (!firma) {
    if (text(row.firstName)) patch.melderVorname = text(row.firstName);
    if (text(row.lastname)) patch.melderName = text(row.lastname);
  }
  const tel = komm("phone") || (row.defaultPhone?.type === "phone" ? text(row.defaultPhone?.value) : "");
  const handy = komm("mobile");
  const email = komm("email");
  if (tel) patch.melderTel = tel;
  if (handy) patch.melderHandy = handy;
  if (email) patch.melderEmail = email;
  return Object.keys(patch).length > 0 ? patch : null;
}
